/**
 * Community-folder discovery and target resolution inside the `communities/` subtree — the
 * counterpart of `base-workspace.ts` for `bases/` (gh #5003).
 *
 * In `lib/` beside its siblings because the scope selector and the hub side's hints need it as
 * well as the communities sync (`communities/sync.ts`).
 *
 * A community folder is `communities/<slug>/`, holding `community.yaml` (which names the
 * community by `community_id`) and `rules.md`. Resolution here is explicit only: a folder name
 * or a community id, or the folder the current directory is in. Unlike the other subtrees there
 * is no sole-folder fallback — a community is never selected without being named.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { communitiesDirLabel, isRealDirectory, resolveLayout, WAYAI_LAYOUT } from './layout.js';
import { expected } from './expected.js';
import { PATH_SAFE_ID_RULE, isPathSafeId } from './base-id.js';
import { isUnder, readFileNoFollow, requireRealSubdirNoSymlink } from './fs-safety.js';
import { UUID_RE } from './utils.js';

/** The per-folder file naming its community, its settings and its rooms. */
export const COMMUNITY_FILE = 'community.yaml';
/** The rules members see, as Markdown; empty for none. */
export const RULES_FILE = 'rules.md';

export interface CommunityFolder {
  folder: string;
  communityId: string;
}

/**
 * The community id a folder's `community.yaml` names, or null when the file is absent,
 * unreadable, not a mapping, or names no UUID. Read no-follow, as every workspace file is.
 */
export function readCommunityId(folder: string): string | null {
  const bytes = readFileNoFollow(folder, path.join(folder, COMMUNITY_FILE));
  if (bytes === null) return null;
  let doc: unknown;
  try {
    doc = yaml.load(bytes.toString('utf-8'));
  } catch {
    return null;
  }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return null;
  const id = (doc as { community_id?: unknown }).community_id;
  return typeof id === 'string' && UUID_RE.test(id) ? id : null;
}

/** Every `communities/<x>/` folder (a real directory, not dot-prefixed) whose file names a community. */
export function listCommunityFolders(communitiesDir: string): CommunityFolder[] {
  let entries: string[];
  try {
    entries = fs.readdirSync(communitiesDir).filter((entry) => !entry.startsWith('.'));
  } catch {
    return [];
  }
  const out: CommunityFolder[] = [];
  for (const name of entries.sort()) {
    const folder = path.join(communitiesDir, name);
    if (!isRealDirectory(folder)) continue;
    const communityId = readCommunityId(folder);
    if (communityId) out.push({ folder, communityId });
  }
  return out;
}

/**
 * An existing folder (its file is read by the caller, which reports what is wrong with it), or a
 * community id no folder holds yet — a first pull names the folder after the community.
 */
export type ResolvedCommunityTarget = { folder: string } | { folder: null; communityId: string };

/** How a community is named on the command line, for the messages that ask for one. */
export const COMMUNITY_SELECTOR_FORMS = `${WAYAI_LAYOUT.communitiesSubdir}/<folder|community_id>, --community <folder|community_id>, or run inside its folder`;

/**
 * Which community folder this invocation targets. The subtree router has already decided the
 * invocation is a community one and passes the selector it resolved (a folder name or a
 * community id); with none, the folder the current directory is in.
 *
 * The selector becomes a filesystem path, so it is validated as one folder name before it is
 * joined, and an existing folder must be a real directory: both refusals come before any
 * request.
 */
export function resolveCommunityTarget(
  gitRoot: string,
  selector: string | undefined,
  cwd: string = process.cwd(),
): ResolvedCommunityTarget {
  const { communitiesDir } = resolveLayout(gitRoot);
  const label = communitiesDirLabel(gitRoot);

  if (selector === undefined) {
    const here = path.resolve(cwd);
    const name = isUnder(communitiesDir, here) ? path.relative(communitiesDir, here).split(path.sep)[0] : '';
    if (!name) {
      const folders = listCommunityFolders(communitiesDir);
      throw expected(
        [
          `Name the community: ${COMMUNITY_SELECTOR_FORMS}.`,
          ...(folders.length > 0
            ? [`Folders in ${label}/:`, ...folders.map((f) => `  ${path.basename(f.folder)} (${f.communityId})`)]
            : []),
        ].join('\n'),
      );
    }
    selector = name;
  }

  if (!isPathSafeId(selector)) {
    throw expected(
      `Invalid community ${JSON.stringify(selector)}: a community is named by one folder name or its id — ${PATH_SAFE_ID_RULE}.`,
    );
  }
  const folder = path.join(communitiesDir, selector);
  // Refuses a symlink (dangling included) or a file in the folder's place, or on the way to it;
  // nothing there yet passes. Walked from the repo root: the communities dir may not exist yet.
  requireRealSubdirNoSymlink(gitRoot, folder, false);
  if (isRealDirectory(folder)) return { folder };
  if (UUID_RE.test(selector)) {
    const match = listCommunityFolders(communitiesDir).find((f) => f.communityId === selector);
    return match ? { folder: match.folder } : { folder: null, communityId: selector };
  }
  throw expected(
    `No community folder ${JSON.stringify(selector)} in ${label}/. To pull a community for the first time, name it by its id ` +
      `(\`wayai communities list\` shows them): wayai pull ${WAYAI_LAYOUT.communitiesSubdir}/<community_id>`,
  );
}

/**
 * The community id a `wayai use` / `wayai unbind` selector names: a UUID is itself; a community
 * folder's name resolves to the id its `community.yaml` names. Anything else is a miss, as on the
 * hub side — a community id is a UUID a person does not type from memory.
 */
export function resolveCommunitySelectorToId(gitRoot: string | null, selector: string): string {
  if (UUID_RE.test(selector)) return selector;
  const communitiesDir = gitRoot ? resolveLayout(gitRoot).communitiesDir : null;
  if (communitiesDir && isPathSafeId(selector)) {
    const id = readCommunityId(path.join(communitiesDir, selector));
    if (id) return id;
  }
  const folders = communitiesDir ? listCommunityFolders(communitiesDir) : [];
  throw expected(
    [
      `No community matching ${JSON.stringify(selector)} found in ${communitiesDirLabel(gitRoot)}/. Pass a community id or a folder name from:`,
      ...folders.map((f) => `  ${path.basename(f.folder)} (${f.communityId})`),
    ].join('\n'),
  );
}

/**
 * The line a hub-side miss adds when the workspace holds community folders: a community is only
 * ever named explicitly, so a bare `pull`/`push` never reaches one (gh #5003, Routing and scope).
 * Null when there are none. Best-effort: a hint never fails the command it decorates.
 */
export function communityFoldersHint(gitRoot: string | null, verb: string): string | null {
  if (!gitRoot) return null;
  let folders: CommunityFolder[];
  try {
    folders = listCommunityFolders(resolveLayout(gitRoot).communitiesDir);
  } catch {
    return null;
  }
  if (folders.length === 0) return null;
  const names = folders.map((f) => path.basename(f.folder)).join(', ');
  return `Community folders are named explicitly (${names}): wayai ${verb} ${WAYAI_LAYOUT.communitiesSubdir}/<folder>`;
}
